import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { workspacesApi } from "../../actions";
import { brand } from "../../theme/businessFlowsTheme";
import { EmptyState, ListSkeleton, Refusal, Surface, dialogPaper, rowSx } from "./WorkspaceUi";
import { accessGrantText, accessMergeText, accessRows, accessTypesText, fullName, idleViews } from "./workspaceFormat";

// What one person actually sees, read from the server's own answer - the
// databases they reach, the view that applies to each, how much of it that
// lets them see, and which of the views they hold gives it. Read-only:
// nothing here changes anything.
export default function AccessDialog({ open, workspace, person, onClose }) {
  const [rows, setRows] = useState(null);
  // Views they hold that give them nothing: no database yet, or one this
  // workspace no longer owns. Said plainly rather than silently dropped.
  const [idle, setIdle] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !person) return undefined;
    let live = true;
    setRows(null);
    setError("");
    (async () => {
      const res = await workspacesApi.userAccess(workspace.id, person.id);
      if (!live) return;
      if (res.success) {
        setRows(accessRows(res.content));
        setIdle(idleViews(res.content));
      } else setError(res.msg || "Couldn't work out what this person sees.");
    })();
    return () => {
      live = false;
    };
  }, [open, workspace.id, person]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>What {person ? fullName(person) : "this person"} sees</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The databases they reach in {workspace.name}, and the view that decides what they see in each.
        </Typography>

        {error && <Refusal sx={{ mb: 2 }}>{error}</Refusal>}
        {!rows && !error && <ListSkeleton rows={2} height={64} />}

        {rows && rows.length === 0 && (
          <Surface>
            <EmptyState icon={<StorageRoundedIcon />} title="No database yet">
              They reach nothing in this workspace. Give them a view on the Views tab — a view carries the database it is for.
            </EmptyState>
          </Surface>
        )}

        {idle.length > 0 && (
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            They also hold {idle.map((view) => view.name).join(", ")}, which {idle.length === 1 ? "gives" : "give"} them nothing: no database yet, or one this
            workspace no longer uses.
          </Typography>
        )}

        {rows && rows.length > 0 && (
          <Surface>
            {rows.map((row, index) => (
              <Box key={row.name} sx={rowSx(!index)}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{row.label}</Typography>
                  <Chip
                    size="small"
                    label={row.mode === "edit" ? "Can edit content" : "Reads only"}
                    sx={{ height: 20, fontWeight: 600, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {row.view ? (
                    <>
                      View: {row.view.name}
                      {row.view.builtIn ? " (built-in)" : ""}
                      {accessTypesText(row) ? ` · ${accessTypesText(row)}` : ""}
                    </>
                  ) : (
                    <>Everything in it, unfiltered{accessTypesText(row) ? ` · ${accessTypesText(row)}` : ""}</>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {accessGrantText(row)}
                </Typography>
                {accessMergeText(row) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {accessMergeText(row)}
                  </Typography>
                )}
              </Box>
            ))}
          </Surface>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
